import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { useLocation as useGeoLocation } from "../lib/location";
import api from "../lib/api";
import {
  Truck, Package, Car, CarTaxiFront, Wrench, CircleDot, Droplets, Bus,
  Construction, Sparkles, Search, MapPin, Clock, Star, ChevronRight, Navigation, Zap,
} from "lucide-react";
import NearbyDriversMap from "../components/NearbyDriversMap";

// The canonical category list — ordered by "everyday use" first.
// Urgent flag drives the small SOS pill.
const CATS = [
  { id: "taxi",      icon: CarTaxiFront },
  { id: "delivery",  icon: Package },
  { id: "moving",    icon: Truck },
  { id: "towing",    icon: Car,          urgent: true },
  { id: "mechanic",  icon: Wrench,       urgent: true },
  { id: "tire",      icon: CircleDot,    urgent: true },
  { id: "carwash",   icon: Droplets },
  { id: "bus",       icon: Bus },
  { id: "luxury",    icon: Sparkles },
  { id: "crane",     icon: Construction },
];

export default function Landing() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { loc, status, request } = useGeoLocation();
  const nav = useNavigate();
  const [nearby, setNearby] = useState([]);
  const [feePct, setFeePct] = useState(0.15);
  const [popular, setPopular] = useState([]);

  useEffect(() => {
    const params = loc ? { lat: loc.lat, lng: loc.lng } : {};
    // Defensive: guarantee we always store arrays in state so downstream
    // `.map()` renders can never throw `TypeError: <var>.map is not a
    // function` if a caching layer / proxy / native HTTP plugin ever
    // hands us a non-array body. Backend already returns arrays for
    // both endpoints (verified live); this only guards the pathological
    // path and does not change Safari/Web behaviour when the response
    // is a proper array.
    api.get("/listings", { params })
      .then((r) => setNearby(Array.isArray(r.data) ? r.data.slice(0, 8) : []))
      .catch(() => {});
    api.get("/config").then((r) => setFeePct(r.data.platform_fee_pct || 0.15)).catch(() => {});
    api.get("/categories/popular", { params: { limit: 4 } })
      .then((r) => setPopular(Array.isArray(r.data) ? r.data : []))
      .catch(() => setPopular([]));
  }, [loc]);

  const CAT_BY_ID = CATS.reduce((acc, c) => { acc[c.id] = c; return acc; }, {});
  // Merge server-ranked popular list with local metadata (icon).
  // Defensive: coerce `popular` to an array *at the crash site* before
  // any `.map()` derivation. The state initializer is already `[]` and
  // the `useEffect` above uses `Array.isArray(...)` when writing, but
  // an older shipped bundle stored a non-array object into this state
  // and produced `TypeError: p.map(e=>e.id)` on iOS at startup. This
  // coercion is a no-op when `popular` is already an array (Safari /
  // Web path unchanged), and turns any pathological non-array value
  // into an empty list so `.map` cannot throw here again.
  const popularList = Array.isArray(popular) ? popular : [];
  const popularIds = new Set(popularList.map((p) => p.id));
  const orderedCats = [
    ...popularList.map((p) => CAT_BY_ID[p.id]).filter(Boolean),
    ...CATS.filter((c) => !popularIds.has(c.id)),
  ];

  const locLabel = loc?.label
    ? loc.label.split(",").slice(0, 2).join(",")
    : (loc ? `${loc.lat.toFixed(4)}, ${loc.lng.toFixed(4)}` : t("landing.setLocation"));
  const isCustomer = user?.role === "customer";
  const requestHref = isCustomer ? "/request" : "/services";

  return (
    <div>
      {/* ─────────────────────────────────────────────────────────────
          DESKTOP HERO — visible only on lg+. Kept lightweight; the app
          on desktop is a preview only, the real product is mobile.
        ───────────────────────────────────────────────────────────── */}
      <section
        className="hidden lg:block relative overflow-hidden border-b border-white/10"
        data-testid="desktop-hero"
      >
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1517596623376-deccebb96b46?w=1920&q=80"
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-ink-bg via-ink-bg/85 to-transparent" />
        </div>
        <div className="relative max-w-[1180px] mx-auto px-8 py-24 grid grid-cols-12 gap-8">
          <div className="col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 px-3 py-1 text-[11px] font-semibold tracking-widest uppercase text-white/80">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-accent animate-pulse" />
              {t("landing.live")}
            </div>
            <h1 className="mt-6 text-6xl xl:text-7xl font-black leading-[0.95] tracking-tight">
              {t("landing.desktopH1a")}
              <br />
              <span className="text-white/50">{t("landing.desktopH1b")}</span>
            </h1>
            <p className="mt-6 text-lg text-white/60 max-w-xl leading-relaxed">
              {t("landing.desktopSub")}
            </p>
            <div className="mt-8 flex gap-3 flex-wrap">
              <Link
                to={requestHref}
                className="pill-btn bg-white text-black rounded-full px-6 py-3.5 text-sm font-bold inline-flex items-center gap-2"
              >
                {t("landing.heroCtaOrder")} <ChevronRight size={16} className="rtl-flip" />
              </Link>
              <Link
                to="/register?role=supplier"
                className="pill-btn border border-white/30 text-white rounded-full px-6 py-3.5 text-sm font-bold hover:bg-white/5"
              >
                {t("landing.heroCtaSupplier")}
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { k: "18 min", v: t("landing.stats.response") },
                { k: "12.4K",   v: t("landing.stats.operators") },
                { k: `${Math.round(feePct * 100)}%`, v: t("landing.desktopFeeLabel") },
              ].map((s, i) => (
                <div key={i}>
                  <div className="text-3xl font-black">{s.k}</div>
                  <div className="text-[10px] uppercase font-bold tracking-widest text-white/50 mt-1">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────
          MOBILE / TABLET — the real product experience.
          Tighter hierarchy: location → hero → search → services →
          social-proof map → featured providers → supplier CTA → footer
        ───────────────────────────────────────────────────────────── */}
      <div className="lg:hidden max-w-[600px] md:max-w-[820px] mx-auto pb-6">
        {/* Location — subtle, borderless chip */}
        <div className="px-4 pt-4 pb-2">
          <button
            onClick={request}
            className="w-full flex items-center gap-3 bg-white/[0.04] hover:bg-white/[0.07] rounded-2xl px-4 py-3 border border-white/[0.08] transition-colors"
            data-testid="location-btn"
          >
            <div className="relative w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
              <Navigation size={13} strokeWidth={2.2} />
              {loc && <span className="absolute -top-0.5 -end-0.5 w-2 h-2 rounded-full bg-brand-accent" />}
            </div>
            <div className="flex-1 text-start min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">
                {loc ? t("landing.currentLocation") : t("landing.location")}
              </div>
              <div className="text-sm font-semibold truncate mt-0.5">{locLabel}</div>
            </div>
            {status === "requesting"
              ? <div className="text-[10px] text-white/50">…</div>
              : <ChevronRight size={16} className="text-white/40 rtl-flip" />}
          </button>
          {status === "denied" && (
            <div className="text-[11px] text-brand-warn mt-2 px-1">{t("landing.locationDenied")}</div>
          )}
        </div>

        {/* Hero — one bold line, one soft line */}
        <section className="px-4 pt-3 pb-4" data-testid="hero">
          <h1 className="text-[34px] font-black leading-[1.05] tracking-tight">
            {t("landing.heroLine1")}
            <br />
            <span className="text-white/40">{t("landing.heroLine2")}</span>
          </h1>
        </section>

        {/* Primary CTA — the Uber "Where to?" search bar */}
        <div className="px-4 pb-6">
          <button
            onClick={() => nav(requestHref)}
            className="pill-btn w-full bg-white text-black rounded-full ps-5 pe-4 py-4 flex items-center gap-3 text-start"
            data-testid="hero-search"
          >
            <Search size={18} strokeWidth={2.4} />
            <span className="flex-1 text-[15px] font-semibold">
              {isCustomer
                ? t("landing.requestCtaPlaceholder", "Where do you need help?")
                : t("landing.searchPlaceholder")}
            </span>
            <ChevronRight size={18} className="text-black/50 rtl-flip" />
          </button>
        </div>

        {/* Services — one clean 4-col grid, no duplicate popular strip.
            Popular categories come first (via server ranking). */}
        <section className="px-4" data-testid="services-grid">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-[15px] font-bold">{t("landing.servicesTitle", "Services")}</h2>
            <Link
              to="/services"
              className="text-[12px] font-semibold text-white/60 hover:text-white"
            >
              {t("landing.seeAll")} →
            </Link>
          </div>
          <div className="grid grid-cols-4 gap-2.5">
            {orderedCats.slice(0, 8).map((c, i) => (
              <Link
                key={c.id}
                to={isCustomer ? `/request?category=${c.id}` : `/services/${c.id}`}
                className="tile-btn relative rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] p-3 flex flex-col items-center gap-2 aspect-square justify-center"
                data-testid={`tile-${c.id}`}
                style={{ animation: `pop-in 0.35s ease-out ${i * 0.03}s both` }}
              >
                {c.urgent && (
                  <span className="absolute top-2 end-2 w-1.5 h-1.5 rounded-full bg-brand-danger" />
                )}
                <c.icon size={22} strokeWidth={2.2} className="text-white" />
                <div className="text-[11px] font-semibold text-center leading-tight text-white/90 line-clamp-1">
                  {t(`categories.${c.id}`)}
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Live nearby drivers — social proof, kept but tighter */}
        {(!user || isCustomer) && (
          <section className="px-4 mt-6" data-testid="landing-nearby-drivers">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-[15px] font-bold">
                {t("landing.nearbyTitle", "Cars around you")}
              </h2>
              <Link
                to={user ? "/request" : "/register"}
                className="text-[12px] font-semibold text-brand-accent hover:text-brand-accent/80"
              >
                {t("landing.requestOne", "Request one")} →
              </Link>
            </div>
            <div className="rounded-2xl overflow-hidden border border-white/[0.08]">
              <NearbyDriversMap height={200} radiusKm={15} />
            </div>
          </section>
        )}

        {/* Featured operators — horizontal scroll, minimal chrome */}
        {nearby.length > 0 && (
          <section className="mt-6" data-testid="featured-section">
            <div className="px-4 flex items-baseline justify-between mb-3">
              <h2 className="text-[15px] font-bold">
                {loc ? t("landing.nearbyTitle") : t("landing.featuredTitle")}
              </h2>
              <Link
                to="/services"
                className="text-[12px] font-semibold text-white/60 hover:text-white"
              >
                {t("landing.seeAll")} →
              </Link>
            </div>
            <div className="flex gap-3 overflow-x-auto no-scrollbar px-4 pb-2">
              {nearby.map((l) => (
                <Link
                  to={`/book/${l.listing_id}`}
                  key={l.listing_id}
                  className="min-w-[220px] max-w-[220px] rounded-2xl overflow-hidden bg-white/[0.04] border border-white/[0.08] hover:border-white/20 transition-colors"
                  data-testid={`featured-${l.listing_id}`}
                >
                  <div className="h-24 bg-white/[0.04] relative">
                    {l.image_url && (
                      <img src={l.image_url} alt="" loading="lazy"
                           className="w-full h-full object-cover" />
                    )}
                    <div className="absolute top-2 start-2 bg-black/70 backdrop-blur px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide">
                      {t(`categories.${l.category}`)}
                    </div>
                    {l.distance_km !== undefined && l.distance_km !== null && (
                      <div className="absolute top-2 end-2 bg-white/95 text-black px-2 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-1">
                        <MapPin size={9} /> {l.distance_km} km
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="text-[14px] font-semibold line-clamp-2 leading-snug min-h-[36px]">
                      {l.title}
                    </div>
                    <div className="flex items-center justify-between mt-2 gap-2 text-[11px] text-white/60">
                      <div className="flex items-center gap-2">
                        <span className="flex items-center gap-1">
                          <Star size={11} className="text-brand-warn" fill="currentColor" />
                          <span className="text-white/80 font-semibold">
                            {l.supplier_rating?.toFixed(1) || "—"}
                          </span>
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={11} /> {l.eta_minutes || 30}m
                        </span>
                      </div>
                      <div className="text-white font-bold">${l.price}</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Supplier CTA — quiet card, no gradient noise */}
        {(!user || user.role !== "supplier") && (
          <section className="px-4 mt-6" data-testid="supplier-cta">
            <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-5">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-accent">
                {t("landing.supplierEyebrow")}
              </div>
              <div className="text-xl font-black mt-1.5">{t("landing.supplierTitleShort")}</div>
              <div className="text-[13px] text-white/60 mt-1.5 leading-relaxed">
                {t("landing.supplierSub")}
              </div>
              <Link
                to="/register?role=supplier"
                className="pill-btn mt-4 inline-flex items-center gap-1.5 bg-white text-black rounded-full px-5 py-2.5 text-[13px] font-bold"
                data-testid="supplier-signup-btn"
              >
                {t("landing.becomeSupplier")} <ChevronRight size={14} className="rtl-flip" />
              </Link>
            </div>
          </section>
        )}

        <footer
          className="px-4 pb-24 pt-6 mt-6 border-t border-white/[0.08]"
          data-testid="landing-footer"
        >
          <div className="text-[11px] text-white/40 text-center flex items-center justify-center gap-3 flex-wrap">
            <span>© {new Date().getFullYear()} Drivons</span>
            <span className="text-white/20">·</span>
            <Link to="/privacy" className="hover:text-white" data-testid="footer-privacy">
              {t("footer.privacy", "Privacy")}
            </Link>
            <span className="text-white/20">·</span>
            <Link to="/terms" className="hover:text-white" data-testid="footer-terms">
              {t("footer.terms", "Terms")}
            </Link>
            <span className="text-white/20">·</span>
            <Link to="/support" className="hover:text-white" data-testid="footer-support">
              {t("footer.support", "Support")}
            </Link>
          </div>
        </footer>
      </div>
    </div>
  );
}
